import React from "react";

/**
 * Illustrations — engraved / archival editorial SVGs.
 * Aesthetic: 19th-century agricultural manual. Crosshatching, parallel-line
 * shadow planes, anatomical detail, three-plane composition.
 *
 * All illustrations share a tobacco/ink palette with no fills (engravings).
 * Hatching is generated procedurally to keep stroke density consistent.
 */

const INK = "#3A2218";
const INK_SOFT = "#6A4A33";
const GOLD = "#9C7B43";
const PAPER = "#F4EFE3";

const baseProps = {
  fill: "none",
  stroke: INK,
  strokeWidth: 1.2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

/* ----------------------------- Hatch helpers ----------------------------- */
function ParallelHatch({
  x1, y1, x2, y2, count = 8, angle = 45, spacing = 4, opacity = 0.35, width = 0.6,
}) {
  // Draw `count` parallel lines spanning a rectangle defined by (x1,y1)-(x2,y2)
  // We draw them as short strokes inside the box, oriented at `angle` degrees.
  const lines = [];
  const rad = (angle * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const w = x2 - x1;
  const h = y2 - y1;
  const len = Math.max(w, h);
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1 || 1);
    const cx = x1 + w * t;
    const cy = y1 + h * t;
    const sx = cx - dx * len * 0.5;
    const sy = cy - dy * len * 0.5;
    const ex = cx + dx * len * 0.5;
    const ey = cy + dy * len * 0.5;
    lines.push(
      <line
        key={i}
        x1={sx}
        y1={sy}
        x2={ex}
        y2={ey}
        stroke={INK}
        strokeWidth={width}
        strokeOpacity={opacity}
      />
    );
  }
  return <g>{lines}</g>;
}

/* ============================================================
 * I. CIGAR STUDY — hand offering a lit cigar with smoke trails
 * Replaces the earlier line-art CigarInHand. Adds:
 *   - Crosshatched smoke volume
 *   - Knuckle/tendon shading on hand
 *   - Ash texture and ember
 *   - Wrapper grain striations
 *   - Engraved band ornament with denticles
 * ============================================================ */
export function CigarStudy({ className = "" }) {
  return (
    <svg viewBox="0 0 700 620" className={className} {...baseProps}>
      <defs>
        <clipPath id="palmShadeClip">
          <path d="M210 300 Q 200 360 215 420 Q 240 470 290 480 L 380 480 Q 430 478 450 440 Q 460 410 450 370 Z" />
        </clipPath>
        <clipPath id="cigarBodyClip">
          <path d="M100 260 L 440 260 Q 460 260 460 280 Q 460 300 440 300 L 100 300 Z" />
        </clipPath>
        <clipPath id="ashClip">
          <ellipse cx="100" cy="280" rx="20" ry="22" />
        </clipPath>
      </defs>

      {/* === Smoke trails (crosshatched volumes) === */}
      <g strokeWidth="1" opacity="0.55">
        {/* Central column */}
        <path d="M150 70 C 140 100, 170 120, 150 160 S 175 220, 155 270" strokeOpacity="0.7" />
        <path d="M170 60 C 160 90, 180 110, 168 150 S 185 215, 168 265" strokeOpacity="0.55" />
        <path d="M125 80 C 120 110, 140 130, 122 170 S 138 230, 118 270" strokeOpacity="0.5" />
        <path d="M195 50 C 195 80, 210 105, 195 140 S 215 200, 195 250" strokeOpacity="0.5" />
        {/* Cross-hatch density at base of plumes */}
        {Array.from({ length: 10 }).map((_, i) => (
          <line
            key={`s1-${i}`}
            x1={130 + i * 7}
            y1={250 - i * 2}
            x2={138 + i * 7}
            y2={258 - i * 2}
            stroke={INK}
            strokeOpacity={0.32}
            strokeWidth={0.5}
          />
        ))}
      </g>

      {/* === Cigar body === */}
      <g>
        {/* Outer contour */}
        <path
          d="M100 260 L 440 260 Q 460 260 460 280 Q 460 300 440 300 L 100 300 Z"
          strokeWidth="1.4"
        />
        {/* Wrapper helix grain */}
        <g clipPath="url(#cigarBodyClip)">
          {Array.from({ length: 22 }).map((_, i) => {
            const x = 110 + i * 16;
            return (
              <path
                key={`grain-${i}`}
                d={`M${x} 261 Q ${x + 5} 280 ${x} 299`}
                strokeOpacity={0.22}
                strokeWidth={0.6}
              />
            );
          })}
          {/* Long horizontal grain */}
          <path d="M110 270 Q 270 268 440 271" strokeOpacity="0.28" strokeWidth="0.6" />
          <path d="M110 290 Q 270 292 440 289" strokeOpacity="0.22" strokeWidth="0.6" />
          {/* Diagonal hatch underbelly shade */}
          <ParallelHatch x1={110} y1={290} x2={440} y2={300} count={28} angle={22} spacing={3} opacity={0.18} width={0.5} />
        </g>

        {/* Ash & ember (left foot) */}
        <g>
          <ellipse cx="100" cy="280" rx="20" ry="22" strokeWidth="1.2" />
          {/* Ash hatching */}
          <g clipPath="url(#ashClip)">
            {Array.from({ length: 18 }).map((_, i) => (
              <line
                key={`ash-${i}`}
                x1={82 + i * 2.2}
                y1={258 + (i % 3) * 2}
                x2={86 + i * 2.2}
                y2={302 - (i % 3) * 2}
                stroke={INK}
                strokeWidth={0.45}
                strokeOpacity={0.55}
              />
            ))}
            {/* Crosshatch */}
            {Array.from({ length: 14 }).map((_, i) => (
              <line
                key={`ashx-${i}`}
                x1={82 + i * 2.6}
                y1={302 - (i % 3) * 2}
                x2={88 + i * 2.6}
                y2={258 + (i % 3) * 2}
                stroke={INK}
                strokeWidth={0.35}
                strokeOpacity={0.35}
              />
            ))}
          </g>
          {/* Ember rim */}
          <path d="M84 270 Q 92 264 100 263" strokeOpacity="0.7" strokeWidth="1" />
          <path d="M84 290 Q 92 296 100 297" strokeOpacity="0.7" strokeWidth="1" />
          {/* Ash flake */}
          <path d="M82 280 L 76 280" strokeOpacity="0.55" />
          <path d="M82 275 L 78 273" strokeOpacity="0.45" />
        </g>

        {/* Cap (right rounded tip) */}
        <path d="M440 260 Q 470 270 470 280 Q 470 290 440 300" strokeWidth="1.3" />
        <path d="M448 270 Q 460 280 448 290" strokeOpacity="0.5" />

        {/* Band — ornate engraved label */}
        <g>
          <rect x="320" y="258" width="80" height="44" strokeWidth="1.2" />
          <line x1="320" y1="266" x2="400" y2="266" stroke={GOLD} strokeWidth="0.9" />
          <line x1="320" y1="294" x2="400" y2="294" stroke={GOLD} strokeWidth="0.9" />
          {/* Denticles */}
          {Array.from({ length: 10 }).map((_, i) => (
            <line
              key={`dent-${i}`}
              x1={324 + i * 8}
              y1={266}
              x2={324 + i * 8}
              y2={269}
              stroke={GOLD}
              strokeWidth={0.5}
            />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line
              key={`dentb-${i}`}
              x1={324 + i * 8}
              y1={291}
              x2={324 + i * 8}
              y2={294}
              stroke={GOLD}
              strokeWidth={0.5}
            />
          ))}
          <text
            x="360"
            y="286"
            textAnchor="middle"
            fontFamily="Cormorant Garamond, serif"
            fontStyle="italic"
            fontSize="14"
            fill={INK}
            stroke="none"
          >
            Ramírez
          </text>
        </g>
      </g>

      {/* === Hand === */}
      <g strokeWidth="1.2">
        {/* Thumb */}
        <path d="M210 300 Q 220 332 240 348 Q 256 358 270 352 L 282 342" />
        <path d="M218 305 Q 230 332 246 346" strokeOpacity="0.45" strokeWidth="0.6" />
        {/* Index */}
        <path d="M250 300 Q 256 336 280 358 Q 300 372 318 366 L 330 354" />
        <path d="M258 305 Q 270 336 290 358" strokeOpacity="0.45" strokeWidth="0.6" />
        {/* Middle */}
        <path d="M295 300 Q 302 348 322 372 Q 344 392 366 386 L 376 376" />
        <path d="M302 308 Q 312 348 334 372" strokeOpacity="0.45" strokeWidth="0.6" />
        {/* Ring */}
        <path d="M340 300 Q 348 354 370 380 Q 388 400 408 396 L 416 386" />
        <path d="M348 308 Q 360 358 384 384" strokeOpacity="0.45" strokeWidth="0.6" />
        {/* Pinky */}
        <path d="M385 300 Q 392 348 404 374 Q 418 392 430 388" />
        <path d="M392 308 Q 402 348 416 372" strokeOpacity="0.45" strokeWidth="0.6" />

        {/* Palm contour */}
        <path d="M210 300 Q 200 360 215 420 Q 240 470 290 480 L 380 480 Q 430 478 450 440 Q 460 410 450 370" />
        {/* Wrist */}
        <path d="M225 480 L 250 510 L 380 510 L 420 480" strokeOpacity="0.85" />

        {/* Knuckle creases */}
        <path d="M255 360 L 270 355" strokeOpacity="0.4" strokeWidth="0.7" />
        <path d="M295 380 L 312 374" strokeOpacity="0.4" strokeWidth="0.7" />
        <path d="M338 396 L 355 390" strokeOpacity="0.4" strokeWidth="0.7" />
        <path d="M380 408 L 396 402" strokeOpacity="0.4" strokeWidth="0.7" />

        {/* Palm shading — parallel hatch */}
        <g clipPath="url(#palmShadeClip)">
          {Array.from({ length: 26 }).map((_, i) => (
            <line
              key={`palm-${i}`}
              x1={205}
              y1={310 + i * 7}
              x2={235 + (i % 4) * 10}
              y2={325 + i * 7}
              stroke={INK}
              strokeWidth={0.5}
              strokeOpacity={0.28}
            />
          ))}
          {/* Crosshatch second pass on lower palm */}
          {Array.from({ length: 14 }).map((_, i) => (
            <line
              key={`palmx-${i}`}
              x1={300 + i * 5}
              y1={420 + (i % 3) * 4}
              x2={335 + i * 5}
              y2={460 + (i % 3) * 4}
              stroke={INK}
              strokeWidth={0.4}
              strokeOpacity={0.22}
            />
          ))}
        </g>

        {/* Cuff edge */}
        <path d="M225 510 Q 240 520 250 525" strokeOpacity="0.6" />
        <path d="M395 510 Q 410 520 420 525" strokeOpacity="0.6" />
      </g>

      {/* Folio number & signature */}
      <g stroke="none" fill={INK} fillOpacity="0.55">
        <text x="20" y="600" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="3">
          PLATE · I
        </text>
        <text x="680" y="600" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="3" textAnchor="end">
          MMXLVIII
        </text>
      </g>
    </svg>
  );
}

/* ============================================================
 * II. ESTATE PANORAMA — Dominican landscape with colonial estate,
 * palm trees, terraced rows, foreground tobacco leaves, distant sierras.
 * Three-plane composition with cross-hatched shading.
 * ============================================================ */
export function EstatePanorama({ className = "" }) {
  return (
    <svg viewBox="0 0 1200 480" className={className} {...baseProps}>
      <defs>
        <clipPath id="skyClip">
          <rect x="0" y="0" width="1200" height="180" />
        </clipPath>
        <clipPath id="midClip">
          <rect x="0" y="180" width="1200" height="120" />
        </clipPath>
        <clipPath id="foreClip">
          <rect x="0" y="300" width="1200" height="180" />
        </clipPath>
      </defs>

      {/* === Sky horizon (very light hatching for atmosphere) === */}
      <g clipPath="url(#skyClip)" opacity="0.18">
        {Array.from({ length: 14 }).map((_, i) => (
          <line key={`sky-${i}`} x1={0} y1={20 + i * 11} x2={1200} y2={20 + i * 11} stroke={INK} strokeWidth={0.4} />
        ))}
      </g>

      {/* === Distant sierras (background plane) === */}
      <g strokeWidth="1">
        <path d="M0 200 L 110 130 L 220 170 L 330 110 L 470 165 L 600 95 L 750 155 L 880 110 L 1000 170 L 1100 130 L 1200 165 L 1200 220 L 0 220 Z" />
        {/* Sierra crosshatch (parallel) */}
        {Array.from({ length: 16 }).map((_, i) => (
          <line
            key={`sierra-${i}`}
            x1={0 + i * 22}
            y1={210 + i * 0.3}
            x2={50 + i * 22}
            y2={170 + i * 0.3}
            stroke={INK}
            strokeOpacity={0.22}
            strokeWidth={0.5}
          />
        ))}
      </g>

      {/* === Mid plane: middle ridge + tree line === */}
      <g strokeWidth="1.05">
        <path d="M0 250 Q 200 220 400 240 T 800 230 T 1200 240 L 1200 280 L 0 280 Z" strokeOpacity="0.85" />
        {/* Tree silhouettes */}
        {[60, 140, 230, 310, 400, 520, 640, 750, 880, 980, 1080, 1170].map((cx, i) => (
          <g key={`tree-${i}`} opacity="0.7">
            <path d={`M${cx} 252 L ${cx} 244`} strokeWidth={0.7} />
            <circle cx={cx} cy={240} r={4 + (i % 3)} strokeWidth={0.7} strokeOpacity={0.55} />
            <line x1={cx - 3} y1={239} x2={cx + 3} y2={239} strokeWidth={0.4} strokeOpacity={0.45} />
          </g>
        ))}
      </g>

      {/* === Colonial estate === */}
      <g strokeWidth="1.1">
        {/* Base */}
        <rect x="430" y="240" width="340" height="100" />
        {/* Roof */}
        <path d="M430 240 L 600 180 L 770 240 Z" />
        {/* Roof tile hatching */}
        <ParallelHatch x1={440} y1={240} x2={760} y2={185} count={18} angle={-25} spacing={3} opacity={0.22} width={0.45} />
        {/* Porch columns */}
        {[450, 510, 570, 630, 690, 750].map((x) => (
          <line key={`col-${x}`} x1={x} y1={340} x2={x} y2={262} strokeWidth={0.85} />
        ))}
        {/* Window panes */}
        {[470, 540, 610, 680, 730].map((x, i) => (
          <g key={`win-${i}`}>
            <rect x={x} y={278} width={20} height={32} strokeWidth={0.7} />
            <line x1={x + 10} y1={278} x2={x + 10} y2={310} strokeWidth={0.4} strokeOpacity={0.6} />
            <line x1={x} y1={294} x2={x + 20} y2={294} strokeWidth={0.4} strokeOpacity={0.6} />
          </g>
        ))}
        {/* Door */}
        <rect x="588" y="290" width="24" height="50" strokeWidth="0.85" />
        {/* Wall hatch shadow under porch */}
        <ParallelHatch x1={430} y1={310} x2={770} y2={340} count={20} angle={28} spacing={3} opacity={0.16} width={0.45} />
      </g>

      {/* === Palm trees === */}
      <g strokeWidth="1.05">
        {[140, 920].map((cx) => (
          <g key={`palm-${cx}`}>
            <path d={`M${cx} 340 Q ${cx + 2} 280 ${cx} 230 Q ${cx - 1} 200 ${cx + 2} 180`} strokeWidth={1} />
            {/* Trunk rings */}
            {Array.from({ length: 7 }).map((_, i) => (
              <line
                key={`pr-${cx}-${i}`}
                x1={cx - 4}
                y1={230 + i * 14}
                x2={cx + 4}
                y2={230 + i * 14}
                strokeOpacity={0.45}
                strokeWidth={0.5}
              />
            ))}
            {/* Fronds */}
            {[-50, -35, -18, 0, 18, 35, 50].map((deg, i) => {
              const rad = (deg * Math.PI) / 180;
              const lx = cx + Math.sin(rad) * 60;
              const ly = 180 - Math.cos(rad) * 40;
              return (
                <g key={`f-${cx}-${i}`}>
                  <path d={`M${cx} 180 Q ${(cx + lx) / 2} ${175 - i * 2} ${lx} ${ly}`} strokeWidth={0.85} />
                  {/* Frond barbs */}
                  {Array.from({ length: 8 }).map((_, j) => {
                    const t = j / 8;
                    const px = cx + (lx - cx) * t;
                    const py = 180 + (ly - 180) * t;
                    return (
                      <line
                        key={`fb-${cx}-${i}-${j}`}
                        x1={px}
                        y1={py}
                        x2={px - 4}
                        y2={py + 5}
                        strokeWidth={0.35}
                        strokeOpacity={0.55}
                      />
                    );
                  })}
                </g>
              );
            })}
          </g>
        ))}
      </g>

      {/* === Terraced field rows (foreground) === */}
      <g strokeWidth="0.85">
        <line x1="0" y1="340" x2="1200" y2="340" strokeWidth={1} />
        {Array.from({ length: 14 }).map((_, i) => {
          const y = 348 + i * 9;
          const op = 0.5 - i * 0.025;
          return (
            <path
              key={`row-${i}`}
              d={`M0 ${y} Q 300 ${y - 3} 600 ${y} T 1200 ${y}`}
              strokeOpacity={op}
            />
          );
        })}
        {/* Crop tufts */}
        {Array.from({ length: 38 }).map((_, i) => {
          const x = 30 + i * 32 + (i % 3) * 8;
          const y = 380 + (i % 4) * 14;
          return (
            <g key={`tuft-${i}`} opacity={0.6}>
              <line x1={x} y1={y} x2={x} y2={y - 6} strokeWidth={0.5} />
              <line x1={x - 2} y1={y - 4} x2={x - 4} y2={y - 8} strokeWidth={0.4} />
              <line x1={x + 2} y1={y - 4} x2={x + 4} y2={y - 8} strokeWidth={0.4} />
            </g>
          );
        })}
      </g>

      {/* === Foreground tobacco leaf cluster (bottom-right) === */}
      <g transform="translate(1080, 460)" strokeWidth="1.15">
        <path d="M0 0 Q -50 -50 -80 -110 Q -40 -130 -10 -120 Q 5 -60 0 0 Z" />
        {/* Vein */}
        <path d="M-10 -120 Q -25 -65 -8 -5" strokeOpacity={0.7} strokeWidth={0.7} />
        {/* Side veins */}
        {Array.from({ length: 6 }).map((_, i) => {
          const t = i / 6;
          const ox = -10 + (-15) * (1 - t);
          const oy = -120 + 110 * t;
          return (
            <path
              key={`v-${i}`}
              d={`M${ox} ${oy} Q ${ox - 18} ${oy + 4} ${ox - 30} ${oy + 12}`}
              strokeOpacity={0.45}
              strokeWidth={0.5}
            />
          );
        })}
        <path d="M5 -10 Q 50 -55 75 -110 Q 40 -132 12 -118 Q 6 -55 5 -10 Z" strokeOpacity={0.85} />
        <path d="M12 -118 Q 30 -60 8 -8" strokeOpacity={0.55} strokeWidth={0.6} />
      </g>

      {/* Bottom rule + signature */}
      <line x1="0" y1="475" x2="1200" y2="475" strokeOpacity={0.35} strokeWidth={0.5} />
    </svg>
  );
}

/* ============================================================
 * III. SOLITARY WORKER — close composition: figure inspecting leaf
 * Used as Heritage / Land secondary plate.
 * ============================================================ */
export function SolitaryWorker({ className = "" }) {
  return (
    <svg viewBox="0 0 360 480" className={className} {...baseProps}>
      {/* Sun line */}
      <line x1="20" y1="60" x2="120" y2="60" strokeOpacity={0.3} strokeWidth={0.5} />
      <line x1="240" y1="60" x2="340" y2="60" strokeOpacity={0.3} strokeWidth={0.5} />
      {/* Hat */}
      <path d="M120 90 Q 180 60 240 90 Q 220 100 180 102 Q 140 100 120 90 Z" strokeWidth="1.2" />
      <path d="M150 90 Q 180 78 210 90" strokeWidth="0.85" strokeOpacity={0.6} />
      {/* Crown shading hatch */}
      <ParallelHatch x1={155} y1={82} x2={205} y2={92} count={10} angle={20} spacing={1.2} opacity={0.3} width={0.4} />
      {/* Face */}
      <path d="M165 100 Q 165 130 175 145 Q 185 152 195 145 Q 205 130 205 100" strokeWidth="1.15" />
      {/* Beard hatching */}
      <ParallelHatch x1={170} y1={130} x2={200} y2={148} count={14} angle={70} spacing={1} opacity={0.4} width={0.4} />
      {/* Neck */}
      <path d="M178 148 L 178 168 M 192 148 L 192 168" />
      {/* Shirt */}
      <path d="M155 168 L 145 240 Q 140 290 145 340 L 215 340 Q 220 290 215 240 L 205 168 Z" />
      {/* Shirt fold lines */}
      <path d="M170 200 Q 180 220 170 250" strokeOpacity={0.4} strokeWidth={0.55} />
      <path d="M190 200 Q 195 220 190 260" strokeOpacity={0.4} strokeWidth={0.55} />
      {/* Sleeve hatching */}
      <ParallelHatch x1={148} y1={210} x2={170} y2={260} count={14} angle={70} spacing={1.4} opacity={0.22} width={0.4} />
      <ParallelHatch x1={195} y1={210} x2={215} y2={260} count={14} angle={-70} spacing={1.4} opacity={0.22} width={0.4} />
      {/* Trousers */}
      <path d="M150 340 Q 148 400 158 460 L 178 460 Q 180 410 178 340" />
      <path d="M210 340 Q 212 400 202 460 L 182 460 Q 180 410 182 340" />
      {/* Boots */}
      <path d="M150 460 L 162 470 L 178 470 L 178 462" />
      <path d="M210 460 L 198 470 L 182 470 L 182 462" />
      {/* Arm holding leaf */}
      <path d="M155 200 Q 130 240 110 290 Q 100 320 110 340 L 130 350" />
      {/* Leaf in hand */}
      <g transform="translate(80, 320)">
        <path d="M0 0 Q -30 -30 -45 -65 Q -22 -78 -5 -70 Q 4 -38 0 0 Z" strokeWidth={1.05} />
        <path d="M-5 -70 Q -16 -38 -3 -3" strokeOpacity={0.6} strokeWidth={0.55} />
        {Array.from({ length: 4 }).map((_, i) => {
          const t = (i + 1) / 5;
          const ox = -5 - 12 * (1 - t);
          const oy = -70 + 70 * t;
          return (
            <path
              key={`lv-${i}`}
              d={`M${ox} ${oy} Q ${ox - 10} ${oy + 3} ${ox - 18} ${oy + 8}`}
              strokeOpacity={0.4}
              strokeWidth={0.45}
            />
          );
        })}
      </g>
      {/* Other arm */}
      <path d="M205 200 Q 220 240 230 280 Q 235 310 220 340 L 200 348" />

      {/* Ground hatch */}
      <line x1="20" y1="473" x2="340" y2="473" strokeOpacity={0.4} strokeWidth={0.5} />
      <ParallelHatch x1={20} y1={465} x2={340} y2={478} count={20} angle={6} spacing={1.5} opacity={0.18} width={0.35} />
    </svg>
  );
}

/* ============================================================
 * IV. CURING BARN — Dominican rancho with hanging tobacco leaves
 * ============================================================ */
export function CuringBarn({ className = "" }) {
  return (
    <svg viewBox="0 0 480 360" className={className} {...baseProps}>
      {/* Roof — thatched */}
      <path d="M40 140 L 240 60 L 440 140 Z" strokeWidth={1.2} />
      {/* Thatch lines */}
      {Array.from({ length: 28 }).map((_, i) => (
        <line
          key={`t-${i}`}
          x1={50 + i * 13}
          y1={140}
          x2={70 + i * 13}
          y2={90 + Math.abs(i - 14) * 3}
          strokeOpacity={0.5}
          strokeWidth={0.5}
        />
      ))}
      {/* Walls — wood planks */}
      <rect x="60" y="140" width="360" height="180" strokeWidth={1.15} />
      {Array.from({ length: 10 }).map((_, i) => (
        <line
          key={`pl-${i}`}
          x1={60 + i * 36}
          y1={140}
          x2={60 + i * 36}
          y2={320}
          strokeOpacity={0.4}
          strokeWidth={0.55}
        />
      ))}
      {/* Open doorway */}
      <rect x="200" y="200" width="80" height="120" strokeWidth={1.05} />
      {/* Hanging tobacco leaves inside doorway */}
      <g>
        <line x1="208" y1="208" x2="272" y2="208" strokeWidth={0.6} strokeOpacity={0.6} />
        {[218, 232, 246, 260].map((x, i) => (
          <g key={`lf-${i}`}>
            <path
              d={`M${x} 210 Q ${x - 3} 250 ${x} 280 Q ${x + 5} 280 ${x + 7} 250 Z`}
              strokeWidth={0.7}
              strokeOpacity={0.7}
            />
            <line x1={x + 1} y1={215} x2={x + 1} y2={278} strokeOpacity={0.4} strokeWidth={0.4} />
          </g>
        ))}
      </g>
      {/* Hanging leaves outside (eaves) */}
      <g>
        <line x1="80" y1="155" x2="180" y2="155" strokeWidth={0.5} strokeOpacity={0.55} />
        {[90, 110, 130, 150, 170].map((x, i) => (
          <g key={`le-${i}`}>
            <path
              d={`M${x} 158 Q ${x - 3} 195 ${x} 215 Q ${x + 4} 215 ${x + 6} 195 Z`}
              strokeWidth={0.65}
              strokeOpacity={0.65}
            />
          </g>
        ))}
        <line x1="300" y1="155" x2="400" y2="155" strokeWidth={0.5} strokeOpacity={0.55} />
        {[310, 330, 350, 370, 390].map((x, i) => (
          <g key={`lr-${i}`}>
            <path
              d={`M${x} 158 Q ${x - 3} 195 ${x} 215 Q ${x + 4} 215 ${x + 6} 195 Z`}
              strokeWidth={0.65}
              strokeOpacity={0.65}
            />
          </g>
        ))}
      </g>
      {/* Window shutters */}
      <rect x="100" y="220" width="40" height="55" strokeWidth="0.85" />
      <line x1="120" y1="220" x2="120" y2="275" strokeWidth={0.45} strokeOpacity={0.5} />
      <rect x="340" y="220" width="40" height="55" strokeWidth="0.85" />
      <line x1="360" y1="220" x2="360" y2="275" strokeWidth={0.45} strokeOpacity={0.5} />

      {/* Wall shadow hatching */}
      <ParallelHatch x1={60} y1={250} x2={200} y2={320} count={20} angle={70} spacing={2} opacity={0.16} width={0.4} />
      <ParallelHatch x1={280} y1={250} x2={420} y2={320} count={20} angle={70} spacing={2} opacity={0.16} width={0.4} />

      {/* Ground */}
      <line x1="20" y1="320" x2="460" y2="320" strokeWidth={0.7} strokeOpacity={0.6} />
      <ParallelHatch x1={20} y1={325} x2={460} y2={345} count={14} angle={6} spacing={2} opacity={0.22} width={0.4} />
    </svg>
  );
}

/* ============================================================
 * V. FERMENTATION PILÓN — pile of fermenting tobacco leaves
 * ============================================================ */
export function FermentationPilon({ className = "" }) {
  return (
    <svg viewBox="0 0 380 300" className={className} {...baseProps}>
      {/* Ground rule */}
      <line x1="20" y1="240" x2="360" y2="240" strokeOpacity={0.5} strokeWidth={0.6} />
      {/* Pile contour */}
      <path d="M70 240 Q 90 110 190 80 Q 290 110 310 240" strokeWidth={1.2} />
      {/* Compressed leaf strata */}
      {Array.from({ length: 7 }).map((_, i) => {
        const y = 240 - 22 * (i + 1);
        const indent = 24 + i * 12;
        return (
          <path
            key={`stra-${i}`}
            d={`M${70 + indent} ${y} Q 190 ${y - 8} ${310 - indent} ${y}`}
            strokeWidth={0.7}
            strokeOpacity={0.5}
          />
        );
      })}
      {/* Visible leaves on top */}
      {Array.from({ length: 14 }).map((_, i) => {
        const x = 100 + i * 16 + (i % 3) * 4;
        const y = 92 + (i % 4) * 4;
        return (
          <g key={`tl-${i}`}>
            <path
              d={`M${x} ${y} Q ${x - 6} ${y + 16} ${x} ${y + 26} Q ${x + 6} ${y + 22} ${x + 5} ${y + 4} Z`}
              strokeWidth={0.55}
              strokeOpacity={0.7}
            />
          </g>
        );
      })}
      {/* Side wrinkles */}
      <ParallelHatch x1={80} y1={160} x2={140} y2={235} count={20} angle={70} spacing={1.5} opacity={0.18} width={0.45} />
      <ParallelHatch x1={240} y1={160} x2={300} y2={235} count={20} angle={-70} spacing={1.5} opacity={0.18} width={0.45} />
      {/* Thermometer rod */}
      <line x1="190" y1="80" x2="190" y2="40" strokeWidth={0.85} />
      <circle cx="190" cy="36" r="4" strokeWidth={0.7} />
      {/* Wisp of steam */}
      <path d="M190 30 C 184 22, 196 18, 188 8" strokeOpacity={0.4} strokeWidth={0.7} />
    </svg>
  );
}

/* ============================================================
 * VI. ROLLING TABLE — torcedor's table with chaveta knife,
 * tobacco bundles, mold press, hand-rolled cigar in progress.
 * ============================================================ */
export function RollingTable({ className = "" }) {
  return (
    <svg viewBox="0 0 480 320" className={className} {...baseProps}>
      {/* Table surface */}
      <path d="M30 200 L 450 200 L 460 280 L 20 280 Z" strokeWidth={1.2} />
      {/* Wood grain hatch */}
      <ParallelHatch x1={30} y1={210} x2={450} y2={270} count={28} angle={2} spacing={2.5} opacity={0.18} width={0.4} />
      {/* Front edge shadow */}
      <line x1="20" y1="280" x2="460" y2="280" strokeWidth={1} />
      <ParallelHatch x1={20} y1={282} x2={460} y2={300} count={10} angle={2} spacing={2.5} opacity={0.22} width={0.4} />

      {/* Chaveta knife (left) */}
      <g>
        <path d="M50 180 Q 100 150 130 175 L 130 195 Q 90 200 50 195 Z" strokeWidth={1.1} />
        <path d="M55 185 Q 95 165 122 180" strokeOpacity={0.5} strokeWidth={0.5} />
        {/* Handle */}
        <rect x="125" y="180" width="42" height="14" strokeWidth={0.85} />
        <ParallelHatch x1={127} y1={181} x2={165} y2={193} count={10} angle={20} spacing={1.2} opacity={0.4} width={0.35} />
      </g>

      {/* Tobacco leaves stacked (center) */}
      <g transform="translate(190, 130)">
        <path d="M0 0 Q -30 30 -50 80 Q -10 90 30 80 Q 30 35 0 0 Z" strokeWidth={1.05} />
        <path d="M0 0 Q -10 35 -25 80" strokeOpacity={0.55} strokeWidth={0.55} />
        <path d="M-5 5 Q 10 30 -5 75" strokeOpacity={0.4} strokeWidth={0.45} />
        {/* Second leaf */}
        <path d="M20 -10 Q 50 20 70 70 Q 35 88 5 78 Q 12 30 20 -10 Z" strokeOpacity={0.85} strokeWidth={0.95} />
        <path d="M22 -8 Q 35 28 55 70" strokeOpacity={0.5} strokeWidth={0.55} />
      </g>

      {/* Cigar being rolled (right side) */}
      <g>
        <path d="M280 180 L 410 180 Q 425 180 425 192 Q 425 204 410 204 L 280 204 Z" strokeWidth={1.1} />
        {/* Rolled wrapper diagonal seam */}
        {Array.from({ length: 14 }).map((_, i) => (
          <line
            key={`sm-${i}`}
            x1={285 + i * 10}
            y1={180}
            x2={295 + i * 10}
            y2={204}
            strokeOpacity={0.3}
            strokeWidth={0.4}
          />
        ))}
        {/* Half-formed band */}
        <rect x="350" y="178" width="32" height="28" strokeWidth={0.85} />
        <line x1="350" y1="184" x2="382" y2="184" stroke={GOLD} strokeWidth={0.5} />
        <line x1="350" y1="200" x2="382" y2="200" stroke={GOLD} strokeWidth={0.5} />
      </g>

      {/* Mold press (background-left) */}
      <g>
        <rect x="35" y="100" width="120" height="42" strokeWidth={0.95} />
        {/* Mold dividers */}
        {[55, 75, 95, 115, 135].map((x) => (
          <line key={`md-${x}`} x1={x} y1={100} x2={x} y2={142} strokeOpacity={0.55} strokeWidth={0.45} />
        ))}
        {/* Cigar tips peeking out */}
        {[45, 65, 85, 105, 125, 145].map((x, i) => (
          <ellipse key={`tp-${i}`} cx={x} cy={100} rx={5} ry={3} strokeOpacity={0.7} strokeWidth={0.55} />
        ))}
      </g>

      {/* Wax seal (right back corner) */}
      <g transform="translate(420, 130)">
        <circle r="14" strokeWidth={0.85} />
        <circle r="10" strokeWidth={0.55} strokeOpacity={0.5} />
        <text x="0" y="3" textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="11" fill={INK} stroke="none">
          ER
        </text>
      </g>
    </svg>
  );
}

/* ============================================================
 * VII. CIGAR ANATOMY (replaces inline SVG in old SectionScience)
 * Engraved horizontal cigar exposing wrapper / binder / filler with
 * crosshatched shadow, anatomical labels handled outside.
 * ============================================================ */
export function CigarAnatomy({ active = "wrapper", className = "" }) {
  const isFiller = ["filler", "ligero", "seco", "volado"].includes(active);
  const isBinder = active === "binder";
  const isWrapper = active === "wrapper";
  return (
    <svg viewBox="0 0 1200 240" className={className} {...baseProps}>
      <defs>
        <clipPath id="anatBody">
          <path d="M120 70 L 1080 70 Q 1158 70 1158 120 Q 1158 170 1080 170 L 120 170 Z" />
        </clipPath>
      </defs>

      {/* Filler (foot) */}
      <g opacity={isFiller ? 1 : 0.55}>
        <ellipse cx="120" cy="120" rx="22" ry="50" strokeWidth={1.15} />
        {Array.from({ length: 14 }).map((_, i) => (
          <line
            key={`f-${i}`}
            x1={102 + (i % 3) * 2}
            y1={75 + i * 6}
            x2={138 - (i % 3) * 2}
            y2={75 + i * 6}
            strokeOpacity={0.55}
            strokeWidth={0.5}
          />
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <line
            key={`fc-${i}`}
            x1={108}
            y1={80 + i * 10}
            x2={132}
            y2={88 + i * 10}
            strokeOpacity={0.4}
            strokeWidth={0.4}
          />
        ))}
      </g>

      {/* Binder flap */}
      <g opacity={isBinder ? 1 : 0.7}>
        <path d="M120 70 L 420 70 L 520 170 L 120 170" strokeWidth={1.1} />
        {/* Leaf veins */}
        <path d="M180 90 Q 300 130 480 158" strokeOpacity={0.45} strokeWidth={0.55} />
        <path d="M210 84 Q 320 118 440 138" strokeOpacity={0.32} strokeWidth={0.5} />
        <path d="M250 78 Q 360 110 460 132" strokeOpacity={0.28} strokeWidth={0.5} />
        {/* Diagonal hatch */}
        <ParallelHatch x1={140} y1={90} x2={500} y2={165} count={28} angle={62} spacing={2.4} opacity={0.16} width={0.4} />
      </g>

      {/* Wrapper */}
      <g opacity={isWrapper ? 1 : 0.78}>
        <path d="M260 70 L 1080 70 Q 1140 70 1140 120 Q 1140 170 1080 170 L 260 170" strokeWidth={1.3} />
        <g clipPath="url(#anatBody)">
          {/* Horizontal grain */}
          {Array.from({ length: 9 }).map((_, i) => (
            <path
              key={`h-${i}`}
              d={`M260 ${82 + i * 11} Q 600 ${88 + i * 11} 1140 ${82 + i * 11}`}
              strokeOpacity={0.18}
              strokeWidth={0.5}
            />
          ))}
          {/* Subtle diagonal hatch shadow underbelly */}
          <ParallelHatch x1={260} y1={150} x2={1140} y2={170} count={36} angle={20} spacing={2.4} opacity={0.18} width={0.4} />
        </g>
      </g>

      {/* Cap */}
      <g>
        <path d="M1080 70 Q 1145 75 1158 120 Q 1145 165 1080 170" strokeWidth={1.25} />
        <path d="M1100 90 Q 1130 110 1100 130" strokeOpacity={0.4} strokeWidth={0.55} />
        <path d="M1110 100 Q 1135 115 1110 130" strokeOpacity={0.3} strokeWidth={0.5} />
      </g>

      {/* Band */}
      <g>
        <rect x="780" y="62" width="148" height="116" strokeWidth={1.1} />
        <line x1="780" y1="78" x2="928" y2="78" stroke={GOLD} strokeWidth={0.85} />
        <line x1="780" y1="162" x2="928" y2="162" stroke={GOLD} strokeWidth={0.85} />
        {/* Denticles */}
        {Array.from({ length: 18 }).map((_, i) => (
          <line key={`d-${i}`} x1={784 + i * 8} y1={78} x2={784 + i * 8} y2={82} stroke={GOLD} strokeWidth={0.5} />
        ))}
        {Array.from({ length: 18 }).map((_, i) => (
          <line key={`db-${i}`} x1={784 + i * 8} y1={158} x2={784 + i * 8} y2={162} stroke={GOLD} strokeWidth={0.5} />
        ))}
        <text
          x="854"
          y="118"
          textAnchor="middle"
          fontFamily="Cormorant Garamond, serif"
          fontStyle="italic"
          fontSize="22"
          fill={INK}
          stroke="none"
        >
          Ramírez
        </text>
        <text
          x="854"
          y="138"
          textAnchor="middle"
          fontFamily="Space Mono, monospace"
          fontSize="7"
          letterSpacing="3"
          fill={GOLD}
          stroke="none"
        >
          MMXLVIII
        </text>
      </g>
    </svg>
  );
}

/* ============================================================
 * VIII. CIGAR LIBRARY — single richly-detailed vertical cigar
 * with ash, foot, foot striations, band, smoke trail.
 * Used in The Collection.
 * ============================================================ */
export function CigarVertical({ className = "", label = "Ramírez", smoke = true }) {
  return (
    <svg viewBox="0 0 140 480" className={className} {...baseProps}>
      {/* Smoke */}
      {smoke && (
        <g opacity="0.55">
          <path d="M70 18 C 64 28, 76 38, 68 52 S 78 76, 70 92" strokeWidth={0.8} />
          <path d="M84 14 C 88 28, 78 40, 86 54 S 80 78, 86 92" strokeWidth={0.65} strokeOpacity={0.6} />
          <path d="M58 22 C 56 32, 64 42, 56 56 S 64 76, 56 90" strokeWidth={0.55} strokeOpacity={0.5} />
        </g>
      )}
      {/* Cap (top) */}
      <path d="M70 96 Q 56 102 56 124" strokeWidth={1.15} />
      <path d="M70 96 Q 84 102 84 124" strokeWidth={1.15} />
      <path d="M70 100 Q 60 106 62 122" strokeOpacity={0.45} strokeWidth={0.5} />
      {/* Body */}
      <rect x="56" y="124" width="28" height="280" strokeWidth={1.2} />
      {/* Wrapper helix */}
      {Array.from({ length: 18 }).map((_, i) => {
        const y = 134 + i * 16;
        return <path key={`hx-${i}`} d={`M56 ${y} Q 70 ${y + 6} 84 ${y}`} strokeOpacity={0.32} strokeWidth={0.5} />;
      })}
      {/* Vertical grain shadow */}
      <ParallelHatch x1={56} y1={140} x2={84} y2={400} count={30} angle={88} spacing={1} opacity={0.12} width={0.35} />
      {/* Band */}
      <rect x="46" y="220" width="48" height="58" strokeWidth={1.1} />
      <line x1="46" y1="232" x2="94" y2="232" stroke={GOLD} strokeWidth={0.65} />
      <line x1="46" y1="266" x2="94" y2="266" stroke={GOLD} strokeWidth={0.65} />
      {Array.from({ length: 6 }).map((_, i) => (
        <line key={`bd-${i}`} x1={50 + i * 8} y1={232} x2={50 + i * 8} y2={234} stroke={GOLD} strokeWidth={0.4} />
      ))}
      {Array.from({ length: 6 }).map((_, i) => (
        <line key={`bdb-${i}`} x1={50 + i * 8} y1={264} x2={50 + i * 8} y2={266} stroke={GOLD} strokeWidth={0.4} />
      ))}
      <text
        x="70"
        y="252"
        textAnchor="middle"
        fontFamily="Cormorant Garamond, serif"
        fontStyle="italic"
        fontSize="11"
        fill={INK}
        stroke="none"
      >
        {label}
      </text>
      <text
        x="70"
        y="266"
        textAnchor="middle"
        fontFamily="Space Mono, monospace"
        fontSize="5"
        letterSpacing="2"
        fill={GOLD}
        stroke="none"
      >
        MMXLVIII
      </text>
      {/* Foot — exposed filler */}
      <ellipse cx="70" cy="424" rx="14" ry="6" strokeWidth={1.05} />
      {/* Foot striations */}
      {Array.from({ length: 7 }).map((_, i) => (
        <line
          key={`fs-${i}`}
          x1={58 + i * 4}
          y1={420}
          x2={58 + i * 4}
          y2={428}
          strokeOpacity={0.5}
          strokeWidth={0.45}
        />
      ))}
      <line x1="56" y1="404" x2="56" y2="420" strokeWidth={1} />
      <line x1="84" y1="404" x2="84" y2="420" strokeWidth={1} />
      {/* Ash plate underneath (fading) */}
      <ellipse cx="70" cy="442" rx="22" ry="3" strokeOpacity={0.35} strokeWidth={0.55} />
    </svg>
  );
}

/* ============================================================
 * IX. PROCESS ICONS — denser engraving versions
 * ============================================================ */
export function ProcessIcon({ kind, className = "" }) {
  const size = { viewBox: "0 0 64 64" };
  switch (kind) {
    case "seed":
      return (
        <svg {...size} className={className} {...baseProps}>
          <ellipse cx="32" cy="36" rx="9" ry="13" strokeWidth={1.1} />
          {Array.from({ length: 6 }).map((_, i) => (
            <line key={i} x1={26 + i * 2} y1={26} x2={28 + i * 2} y2={48} strokeOpacity={0.4} strokeWidth={0.4} />
          ))}
          <path d="M32 24 Q 32 16 38 12" strokeWidth={0.85} />
          <path d="M30 22 Q 28 20 25 19" strokeOpacity={0.55} strokeWidth={0.5} />
          <path d="M32 50 L 32 58" strokeOpacity={0.45} strokeWidth={0.5} />
        </svg>
      );
    case "cultivation":
      return (
        <svg {...size} className={className} {...baseProps}>
          <path d="M32 50 L 32 16" strokeWidth={1.1} />
          <path d="M32 26 Q 18 20 12 10" strokeWidth={0.95} />
          <path d="M32 28 Q 46 22 52 12" strokeWidth={0.95} />
          <path d="M32 38 Q 22 34 16 30" strokeOpacity={0.6} strokeWidth={0.7} />
          <path d="M32 38 Q 42 34 48 30" strokeOpacity={0.6} strokeWidth={0.7} />
          <path d="M32 22 Q 26 18 22 14" strokeOpacity={0.5} strokeWidth={0.5} />
          <path d="M32 22 Q 38 18 42 14" strokeOpacity={0.5} strokeWidth={0.5} />
          <line x1="14" y1="54" x2="50" y2="54" strokeOpacity={0.5} strokeWidth={0.6} />
          <ParallelHatch x1={14} y1={54} x2={50} y2={62} count={10} angle={0} spacing={0.8} opacity={0.2} width={0.35} />
        </svg>
      );
    case "harvest":
      return (
        <svg {...size} className={className} {...baseProps}>
          <path d="M14 14 Q 30 12 36 30 Q 28 40 14 36 Z" strokeWidth={1.1} />
          <path d="M14 14 Q 22 22 36 30" strokeOpacity={0.55} strokeWidth={0.55} />
          <path d="M16 18 Q 22 24 32 30" strokeOpacity={0.4} strokeWidth={0.45} />
          <path d="M18 22 Q 24 26 30 30" strokeOpacity={0.35} strokeWidth={0.4} />
          <path d="M38 30 L 54 50" strokeWidth={1.05} />
          <path d="M52 46 L 56 50 L 52 54" strokeWidth={0.9} />
          <line x1="40" y1="35" x2="50" y2="48" strokeOpacity={0.5} strokeWidth={0.5} />
        </svg>
      );
    case "curing":
      return (
        <svg {...size} className={className} {...baseProps}>
          <path d="M10 50 L 10 22 L 32 10 L 54 22 L 54 50 Z" strokeWidth={1.1} />
          <path d="M10 22 L 54 22" strokeOpacity={0.5} strokeWidth={0.55} />
          {/* hanging leaves */}
          <line x1="20" y1="22" x2="44" y2="22" strokeOpacity={0.5} strokeWidth={0.4} />
          {[24, 32, 40].map((x, i) => (
            <path key={i} d={`M${x} 24 Q ${x - 2} 36 ${x} 44 Q ${x + 3} 44 ${x + 4} 36 Z`} strokeOpacity={0.7} strokeWidth={0.5} />
          ))}
          {/* roof thatch */}
          <ParallelHatch x1={12} y1={10} x2={54} y2={22} count={10} angle={-30} spacing={1.4} opacity={0.3} width={0.35} />
          <rect x="28" y="34" width="8" height="16" strokeWidth={0.55} strokeOpacity={0.6} />
        </svg>
      );
    case "fermentation":
      return (
        <svg {...size} className={className} {...baseProps}>
          {/* Pilón */}
          <path d="M10 50 Q 14 24 32 18 Q 50 24 54 50" strokeWidth={1.1} />
          {Array.from({ length: 4 }).map((_, i) => (
            <path key={i} d={`M${14 + i * 3} ${44 - i * 6} Q 32 ${48 - i * 6 - 4} ${50 - i * 3} ${44 - i * 6}`} strokeOpacity={0.5} strokeWidth={0.45} />
          ))}
          <line x1="32" y1="18" x2="32" y2="6" strokeOpacity={0.7} strokeWidth={0.55} />
          <circle cx="32" cy="5" r="2" strokeOpacity={0.55} strokeWidth={0.45} />
        </svg>
      );
    case "aging":
      return (
        <svg {...size} className={className} {...baseProps}>
          <path d="M10 14 Q 32 8 54 14 L 54 50 Q 32 56 10 50 Z" strokeWidth={1.1} />
          <path d="M10 32 Q 32 26 54 32" strokeOpacity={0.45} strokeWidth={0.5} />
          {Array.from({ length: 5 }).map((_, i) => (
            <line key={i} x1={14 + i * 9} y1={14} x2={14 + i * 9} y2={50} strokeOpacity={0.4} strokeWidth={0.4} />
          ))}
          {/* rivets */}
          {[18, 32, 46].map((x) => (
            <circle key={x} cx={x} cy={14 - 1} r={1} strokeWidth={0.4} strokeOpacity={0.7} />
          ))}
        </svg>
      );
    case "blending":
      return (
        <svg {...size} className={className} {...baseProps}>
          {[10, 20, 30, 40, 50].map((x, i) => (
            <path key={i} d={`M${x} ${10 + (i % 3) * 4} L ${x} 50`} strokeWidth={0.95} />
          ))}
          <path d="M14 26 Q 32 22 50 26" strokeOpacity={0.5} strokeWidth={0.5} />
          <path d="M14 36 Q 32 32 50 36" strokeOpacity={0.4} strokeWidth={0.45} />
          <line x1="6" y1="50" x2="58" y2="50" strokeOpacity={0.6} strokeWidth={0.6} />
        </svg>
      );
    case "rolling":
      return (
        <svg {...size} className={className} {...baseProps}>
          {/* Cigar in progress */}
          <path d="M10 30 L 50 30" strokeWidth={1.05} />
          <ellipse cx="50" cy="30" rx="4" ry="4" strokeOpacity={0.6} strokeWidth={0.6} />
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={i} x1={12 + i * 5} y1={28} x2={16 + i * 5} y2={32} strokeOpacity={0.35} strokeWidth={0.4} />
          ))}
          {/* Hand */}
          <path d="M18 42 Q 14 36 16 30 Q 14 24 18 18" strokeWidth={0.95} />
          <path d="M14 24 Q 8 24 8 30 Q 8 36 14 36" strokeOpacity={0.55} strokeWidth={0.5} />
          {/* Chaveta knife edge */}
          <path d="M28 46 L 38 50 L 50 46" strokeOpacity={0.55} strokeWidth={0.55} />
        </svg>
      );
    case "packaging":
      return (
        <svg {...size} className={className} {...baseProps}>
          <path d="M14 20 L 50 20 L 50 50 L 14 50 Z" strokeWidth={1.1} />
          <path d="M14 20 L 20 12 L 56 12 L 50 20" strokeWidth={1} />
          <line x1="22" y1="28" x2="42" y2="28" strokeOpacity={0.5} strokeWidth={0.5} />
          <line x1="22" y1="36" x2="42" y2="36" strokeOpacity={0.4} strokeWidth={0.45} />
          <text x="32" y="48" textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="9" fill={INK} stroke="none">
            ER
          </text>
          <ParallelHatch x1={14} y1={20} x2={50} y2={50} count={12} angle={70} spacing={2} opacity={0.1} width={0.35} />
        </svg>
      );
    case "final":
      return (
        <svg {...size} className={className} {...baseProps}>
          <path d="M32 12 C 26 18, 38 24, 30 32 S 38 44, 32 52" strokeWidth={0.85} strokeOpacity={0.7} />
          <path d="M22 16 C 18 24, 26 28, 22 34 S 26 44, 22 50" strokeOpacity={0.5} strokeWidth={0.6} />
          <path d="M40 18 C 44 24, 38 28, 42 34 S 38 42, 40 48" strokeOpacity={0.5} strokeWidth={0.6} />
          {/* small glass underneath */}
          <path d="M22 54 L 20 60 L 44 60 L 42 54 Z" strokeWidth={0.7} strokeOpacity={0.6} />
        </svg>
      );
    default:
      return null;
  }
}

/* ============================================================
 * X. MANUSCRIPT ORNAMENT — open journal with quill, inkwell, leaf bookmark
 * Used as ARCHIVE chapter header.
 * ============================================================ */
export function ManuscriptOrnament({ className = "" }) {
  return (
    <svg viewBox="0 0 480 320" className={className} {...baseProps}>
      {/* Base shadow */}
      <line x1="40" y1="280" x2="440" y2="280" strokeOpacity={0.5} strokeWidth={0.6} />
      <ParallelHatch x1={60} y1={282} x2={420} y2={300} count={14} angle={4} spacing={2} opacity={0.18} width={0.35} />

      {/* Open book */}
      <g strokeWidth={1.2}>
        {/* Pages */}
        <path d="M60 240 L 240 230 L 240 90 L 60 100 Z" />
        <path d="M240 230 L 420 240 L 420 100 L 240 90 Z" />
        {/* Spine */}
        <line x1="240" y1="90" x2="240" y2="230" strokeOpacity={0.7} />
        {/* Page edge */}
        <path d="M60 100 L 50 110 L 50 250 L 60 240" strokeWidth={1} />
        <path d="M420 100 L 430 110 L 430 250 L 420 240" strokeWidth={1} />
      </g>
      {/* Text lines on pages */}
      {Array.from({ length: 9 }).map((_, i) => (
        <g key={`tx-${i}`}>
          <line x1={80} y1={120 + i * 12} x2={222} y2={119 + i * 12} strokeOpacity={0.32} strokeWidth={0.4} />
          <line x1={258} y1={119 + i * 12} x2={400} y2={120 + i * 12} strokeOpacity={0.32} strokeWidth={0.4} />
        </g>
      ))}
      {/* First-line ornamental capital */}
      <text
        x="80"
        y="130"
        fontFamily="Cormorant Garamond, serif"
        fontStyle="italic"
        fontSize="24"
        fill={INK}
        stroke="none"
      >
        E
      </text>

      {/* Inkwell (left) */}
      <g strokeWidth={1.05}>
        <rect x="20" y="225" width="34" height="30" />
        <rect x="22" y="218" width="30" height="8" />
        <line x1="20" y1="240" x2="54" y2="240" strokeOpacity={0.4} strokeWidth={0.45} />
        <ParallelHatch x1={22} y1={232} x2={54} y2={252} count={10} angle={70} spacing={1.4} opacity={0.18} width={0.35} />
      </g>

      {/* Quill rising from inkwell */}
      <g strokeWidth={1}>
        <path d="M40 222 L 130 80" strokeWidth={1.1} />
        {/* Feather barbs */}
        {Array.from({ length: 14 }).map((_, i) => {
          const t = i / 13;
          const x = 40 + (130 - 40) * t;
          const y = 222 - (222 - 80) * t;
          return (
            <line
              key={`q-${i}`}
              x1={x}
              y1={y}
              x2={x - 12}
              y2={y - 4}
              strokeOpacity={0.45}
              strokeWidth={0.45}
            />
          );
        })}
        {/* Tip */}
        <path d="M132 78 Q 138 72 134 88" strokeOpacity={0.7} strokeWidth={0.55} />
      </g>

      {/* Tobacco leaf bookmark */}
      <g transform="translate(330, 100) rotate(15)" strokeWidth={1.05}>
        <path d="M0 0 Q -10 60 -2 130 Q 6 130 12 60 Q 6 0 0 0 Z" />
        <line x1="0" y1="2" x2="5" y2="128" strokeOpacity={0.55} strokeWidth={0.5} />
        {Array.from({ length: 5 }).map((_, i) => {
          const t = (i + 1) / 6;
          const ox = -2 - 4 * (1 - t);
          const oy = 2 + 126 * t;
          return (
            <path
              key={i}
              d={`M${ox} ${oy} Q ${ox - 6} ${oy + 2} ${ox - 10} ${oy + 4}`}
              strokeOpacity={0.4}
              strokeWidth={0.4}
            />
          );
        })}
      </g>

      {/* Wax seal (bottom-right) */}
      <g transform="translate(420, 250)" strokeWidth={1.1}>
        <circle r="22" />
        <circle r="16" strokeOpacity={0.55} strokeWidth={0.55} />
        <text x="0" y="5" textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="18" fill={INK} stroke="none">
          ER
        </text>
        {/* Drip */}
        <path d="M-10 18 L -14 30 L -8 32 Z" strokeOpacity={0.5} strokeWidth={0.6} />
        <path d="M12 18 L 16 32 L 22 28 Z" strokeOpacity={0.5} strokeWidth={0.6} />
      </g>
    </svg>
  );
}

/* ============================================================
 * XI. WAX SEAL standalone (heritage closing ornament)
 * ============================================================ */
export function WaxSeal({ className = "" }) {
  return (
    <svg viewBox="0 0 140 140" className={className} {...baseProps}>
      <circle cx="70" cy="70" r="58" strokeWidth={1.2} />
      <circle cx="70" cy="70" r="48" strokeOpacity={0.55} strokeWidth={0.65} />
      <circle cx="70" cy="70" r="42" strokeOpacity={0.4} strokeWidth={0.55} />
      {/* Crown of laurel */}
      {Array.from({ length: 24 }).map((_, i) => {
        const ang = (i / 24) * Math.PI * 2;
        const x1 = 70 + Math.cos(ang) * 48;
        const y1 = 70 + Math.sin(ang) * 48;
        const x2 = 70 + Math.cos(ang) * 56;
        const y2 = 70 + Math.sin(ang) * 56;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeOpacity={0.55} strokeWidth={0.5} />;
      })}
      {/* ER monogram */}
      <text
        x="70"
        y="80"
        textAnchor="middle"
        fontFamily="Cormorant Garamond, serif"
        fontStyle="italic"
        fontSize="40"
        fill={INK}
        stroke="none"
      >
        ER
      </text>
      <text
        x="70"
        y="110"
        textAnchor="middle"
        fontFamily="Space Mono, monospace"
        fontSize="6"
        letterSpacing="3"
        fill={INK}
        fillOpacity="0.6"
        stroke="none"
      >
        MCMXLVIII
      </text>
    </svg>
  );
}

/* ============================================================
 * XII. SUPPORTING ORNAMENTS (kept lean — Hero/Footer ornaments)
 * ============================================================ */
export function InkBottle({ className = "" }) {
  return (
    <svg viewBox="0 0 200 200" className={className} {...baseProps}>
      <rect x="50" y="110" width="68" height="70" strokeWidth={1.15} />
      <rect x="62" y="100" width="44" height="10" strokeWidth={1} />
      <line x1="50" y1="130" x2="118" y2="130" strokeOpacity={0.5} strokeWidth={0.6} />
      <ParallelHatch x1={50} y1={140} x2={118} y2={180} count={14} angle={70} spacing={2} opacity={0.16} width={0.35} />
      {/* Quill */}
      <path d="M104 105 L 188 28" strokeWidth={1.05} />
      {Array.from({ length: 12 }).map((_, i) => {
        const t = i / 11;
        const x = 104 + (188 - 104) * t;
        const y = 105 - (105 - 28) * t;
        return (
          <line key={i} x1={x} y1={y} x2={x - 11} y2={y - 4} strokeOpacity={0.45} strokeWidth={0.4} />
        );
      })}
      <path d="M188 28 Q 196 22 192 38" strokeOpacity={0.6} strokeWidth={0.55} />
      {/* Folded paper */}
      <path d="M16 158 L 28 146 L 144 146 L 132 158 Z" strokeOpacity={0.65} strokeWidth={0.85} />
      <line x1="32" y1="150" x2="140" y2="150" strokeOpacity={0.3} strokeWidth={0.4} />
    </svg>
  );
}
